var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _I18nDropdown_instances, initHoverMode_fn, handleFocusAccessibility_fn;
import { i as isMobile, e as isTablet } from "./utilities-2Suih3Nm.js";
import { i as isKeyboardUser } from "./focus-D9T8n_CN.js";
const _I18nDropdown = class _I18nDropdown extends HTMLElement {
  constructor() {
    var _a;
    super();
    __privateAdd(this, _I18nDropdown_instances);
    this.trigger = this.querySelector(".i18n-trigger");
    this.dropdown = this.querySelector(".i18n-drop");
    this.dropInner = (_a = this.dropdown) == null ? void 0 : _a.querySelector(".i18n-drop-inner");
    this.itemList = this.querySelectorAll(".i18n-drop-item-link");
    this.hiddenInput = this.querySelector(".i18n-hidden-input");
    this.form = this.querySelector(".localization-form");
    this.currentItemFocusedIndex = -1;
    this._dropdownPlaceholder = document.createComment("i18n-drop-portal");
    this._isPortaled = false;
    this._resizeRafId = null;
    this._lastResizeTime = 0;
    this._RESIZE_SETTLE_DELAY = 100;
  }
  static closeAllDropdowns() {
    document.querySelectorAll("dropdown-localization-component").forEach((comp) => {
      var _a;
      if ((_a = comp.dropdown) == null ? void 0 : _a.classList.contains("active")) comp.closeDropdown();
    });
  }
  connectedCallback() {
    this.init();
  }
  disconnectedCallback() {
    this._returnFromPortal();
    this._removeClickOutside();
    this._removeScrollListener();
    if (this._handleResize) {
      window.removeEventListener("resize", this._handleResize);
    }
    if (this._resizeRafId) {
      cancelAnimationFrame(this._resizeRafId);
      this._resizeRafId = null;
    }
  }
  // ── Portal ──────────────────────────────────────────────────
  get isPortalMode() {
    const el = this.closest("[data-portal-mode]");
    if (el && el.getAttribute("data-portal-mode") === "false") return false;
    if (isMobile() || isTablet()) return true;
    return true;
  }
  _sendToPortal() {
    if (!this.isPortalMode) return;
    if (this._isPortaled || !this.dropdown) return;
    const hasServerColor = Array.from(this.dropdown.classList).some((c) => c.startsWith("color-") && c !== "color-");
    if (!hasServerColor) {
      this._portaledSchemeClass = this._getInheritedColorScheme();
    } else {
      this._portaledSchemeClass = null;
    }
    this.dropdown.parentNode.insertBefore(this._dropdownPlaceholder, this.dropdown);
    document.body.appendChild(this.dropdown);
    this.dropdown.classList.add("i18n-portal");
    if (this._portaledSchemeClass) {
      this.dropdown.classList.add(this._portaledSchemeClass);
    }
    this._isPortaled = true;
    this._portalGap = null;
  }
  /** 
   * Start from the nearest parent element that has a class starting with 'color-' 
   */
  _getInheritedColorScheme() {
    let current = this.closest(".localization-block-outer") || this;
    const stickyAnchor = current.closest("[data-sticky-color-scheme]");
    if (stickyAnchor) {
      const scheme = stickyAnchor.getAttribute("data-sticky-color-scheme");
      if (scheme) return `color-${scheme}`;
    }
    while (current && current !== document.documentElement) {
      const classList = Array.from(current.classList);
      const colorClass = classList.find((c) => c.startsWith("color-") && c !== "color-");
      if (colorClass) return colorClass;
      current = current.parentElement;
    }
    return null;
  }
  _returnFromPortal() {
    if (!this._isPortaled || !this.dropdown) return;
    if (this._dropdownPlaceholder.parentNode) {
      this._dropdownPlaceholder.parentNode.insertBefore(this.dropdown, this._dropdownPlaceholder);
      this._dropdownPlaceholder.remove();
    }
    this.dropdown.classList.remove("i18n-portal");
    if (this._portaledSchemeClass) {
      this.dropdown.classList.remove(this._portaledSchemeClass);
      this._portaledSchemeClass = null;
    }
    this.dropdown.style.top = "";
    this.dropdown.style.left = "";
    this._isPortaled = false;
    this._portalGap = 0;
  }
  _updatePosition() {
    if (!this.trigger || !this.dropdown) return;
    if (this._portalGap === null || this._portalGap === void 0) {
      const rawGap = getComputedStyle(this.dropdown).getPropertyValue("--small-gap").trim() || "0";
      this._portalGap = parseFloat(rawGap) * (rawGap.includes("rem") ? 16 : 1);
    }
    const anchorRect = this.getBoundingClientRect();
    const dropRect = this.dropdown.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = this._portalGap;
    if (this.isPortalMode && this._isPortaled) {
      let top = anchorRect.bottom + gap;
      let left = anchorRect.left;
      if (left + dropRect.width > vw - 10) left = anchorRect.right - dropRect.width;
      if (left < 10) left = 10;
      if (top + dropRect.height > vh - 10) top = anchorRect.top - dropRect.height - gap;
      this.dropdown.style.top = `${top}px`;
      this.dropdown.style.left = `${left}px`;
    }
  }
  // ── Scroll / Resize listeners ───────────────────────────────
  _addScrollListener() {
    this._scrollTicking = false;
    this._scrollHandler = () => {
      if (!this._scrollTicking) {
        this._scrollTicking = true;
        requestAnimationFrame(() => {
          this._scrollTicking = false;
          if (this.dropdown.classList.contains("active")) this._updatePosition();
        });
      }
    };
    window.addEventListener("scroll", this._scrollHandler, { passive: true, capture: true });
  }
  _removeScrollListener() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler, { capture: true });
      this._scrollHandler = null;
    }
  }
  _setupResizeListener() {
    this._handleResize = () => {
      if (!this.dropdown.classList.contains("active")) return;
      this._lastResizeTime = performance.now();
      if (this._resizeRafId) cancelAnimationFrame(this._resizeRafId);
      this._resizeRafId = requestAnimationFrame(() => this._checkResizeSettled());
    };
    window.addEventListener("resize", this._handleResize, { passive: true });
  }
  _checkResizeSettled() {
    if (performance.now() - this._lastResizeTime < this._RESIZE_SETTLE_DELAY) {
      this._resizeRafId = requestAnimationFrame(() => this._checkResizeSettled());
      return;
    }
    this._updatePosition();
    this._resizeRafId = null;
  }
  // ── Click-outside management ────────────────────────────────
  _addClickOutside() {
    if (this._clickOutsideHandler) return;
    this._clickOutsideHandler = (e) => {
      if (!this.contains(e.target) && !this.dropdown.contains(e.target)) {
        this.closeDropdown();
      }
    };
    document.addEventListener("click", this._clickOutsideHandler);
  }
  _removeClickOutside() {
    if (this._clickOutsideHandler) {
      document.removeEventListener("click", this._clickOutsideHandler);
      this._clickOutsideHandler = null;
    }
  }
  // ── Open / Close ────────────────────────────────────────────
  openDropdown() {
    _I18nDropdown.closeAllDropdowns();
    if (this.isPortalMode) {
      this._sendToPortal();
    }
    this.classList.add("i18n-active");
    this.dispatchEvent(new CustomEvent("dropdown:open", { bubbles: true }));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._updatePosition();
        this.dropdown.classList.add("active");
      });
    });
    this._addScrollListener();
    if (!this._handleResize) this._setupResizeListener();
    this._addClickOutside();
  }
  closeDropdown() {
    if (!this.dropdown.classList.contains("active")) return;
    this.classList.remove("i18n-active");
    this.dropdown.classList.remove("active");
    this.dispatchEvent(new CustomEvent("dropdown:close", { bubbles: true }));
    this._removeScrollListener();
    this._removeClickOutside();
    const onDone = () => {
      clearTimeout(fallback);
      this.dropdown.removeEventListener("transitionend", onEnd);
      this._returnFromPortal();
    };
    const onEnd = (e) => {
      if (e.target === this.dropInner) onDone();
    };
    this.dropdown.addEventListener("transitionend", onEnd);
    const fallback = setTimeout(onDone, 350);
  }
  // ── Init ────────────────────────────────────────────────────
  init() {
    if (!(this.trigger && this.dropdown && this.itemList.length > 1)) return;
    this.trigger.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const triggerAttr = (this.getAttribute("data-trigger") || "click").toLowerCase();
      const useClick = isMobile() || isTablet() || triggerAttr === "click";
      if (!useClick) return;
      const isActive = this.dropdown.classList.contains("active");
      _I18nDropdown.closeAllDropdowns();
      if (!isActive) this.openDropdown();
    });
    __privateMethod(this, _I18nDropdown_instances, initHoverMode_fn).call(this);
    this.addEventListener("keydown", (e) => __privateMethod(this, _I18nDropdown_instances, handleFocusAccessibility_fn).call(this, e));
    this.trigger.addEventListener("focusin", () => {
      if (isMobile() || isTablet() || !isKeyboardUser()) return;
      if (this.dropdown && !this.dropdown.classList.contains("active")) {
        _I18nDropdown.closeAllDropdowns();
        this.openDropdown();
      }
    });
    const handleFocusOut = () => {
      if (isMobile() || isTablet()) return;
      setTimeout(() => {
        const active = document.activeElement;
        if (!active) return;
        const inComponent = this.contains(active);
        const inDropdown = this.dropdown && this.dropdown.contains(active);
        if (!inComponent && !inDropdown) {
          this.closeDropdown();
        }
      }, 10);
    };
    this.addEventListener("focusout", handleFocusOut);
    if (this.dropdown) {
      this.dropdown.addEventListener("focusout", handleFocusOut);
    }
    this.itemList.forEach((item) => {
      item.addEventListener("click", (e) => {
        var _a;
        e.preventDefault();
        const value = item.getAttribute("data-value");
        if (this.hiddenInput) this.hiddenInput.value = value;
        this.itemList.forEach((el) => {
          var _a2;
          return (_a2 = el.closest(".i18n-drop-item")) == null ? void 0 : _a2.classList.remove("active");
        });
        (_a = item.closest(".i18n-drop-item")) == null ? void 0 : _a.classList.add("active");
        this.closeDropdown();
        if (this.form) this.form.submit();
      });
    });
  }
};
_I18nDropdown_instances = new WeakSet();
// ── Hover mode ──────────────────────────────────────────────
initHoverMode_fn = function() {
  let openT, closeT;
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 280;
  const isHoverMode = () => {
    const triggerAttr = (this.getAttribute("data-trigger") || "click").toLowerCase();
    return !isMobile() && !isTablet() && triggerAttr === "hover";
  };
  const cancelClose = () => clearTimeout(closeT);
  const cancelOpen = () => clearTimeout(openT);
  const scheduleClose = () => {
    if (!isHoverMode()) return;
    cancelOpen();
    closeT = setTimeout(() => {
      closeT = null;
      const isOverTrigger = this.trigger && this.trigger.matches(":hover");
      const isOverDropdown = this.dropdown && this.dropdown.matches(":hover");
      if (!isOverTrigger && !isOverDropdown) {
        this.closeDropdown();
      }
    }, CLOSE_DELAY);
  };
  this.trigger.addEventListener("mouseenter", () => {
    if (!isHoverMode()) return;
    cancelClose();
    if (this.dropdown.classList.contains("active") || this._isPortaled) return;
    openT = setTimeout(() => {
      openT = null;
      this.openDropdown();
    }, OPEN_DELAY);
  });
  this.trigger.addEventListener("mouseleave", () => {
    if (!isHoverMode()) return;
    scheduleClose();
  });
  if (this.dropdown) {
    this.dropdown.addEventListener("mouseenter", () => {
      if (!isHoverMode()) return;
      cancelClose();
    });
    this.dropdown.addEventListener("mouseleave", () => {
      if (!isHoverMode()) return;
      if (this.dropdown.classList.contains("active")) scheduleClose();
    });
  }
};
// ── Keyboard a11y ───────────────────────────────────────────
handleFocusAccessibility_fn = function(e) {
  var _a, _b;
  if (e.target instanceof HTMLAnchorElement && e.key === "Enter" || e.key === " ") return;
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    const isActive = this.dropdown.classList.contains("active");
    _I18nDropdown.closeAllDropdowns();
    if (!isActive) {
      this.openDropdown();
      this.currentItemFocusedIndex = -1;
    }
  }
  if (e.key === "Escape") {
    this.closeDropdown();
    (_a = this.trigger) == null ? void 0 : _a.focus();
  }
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    if (!this.dropdown.classList.contains("active")) return;
    let nextIndex = e.key === "ArrowDown" ? this.currentItemFocusedIndex + 1 : this.currentItemFocusedIndex - 1;
    if (nextIndex >= this.itemList.length) nextIndex = 0;
    if (nextIndex < 0) nextIndex = this.itemList.length - 1;
    (_b = this.itemList[nextIndex]) == null ? void 0 : _b.focus();
    this.currentItemFocusedIndex = nextIndex;
  }
};
let I18nDropdown = _I18nDropdown;
if (!customElements.get("dropdown-localization-component")) {
  customElements.define("dropdown-localization-component", I18nDropdown);
}
